import { useState } from 'react';
import { Bug, ExternalLink, HelpCircle, Lightbulb, Users } from 'lucide-react';
import { useSendFeedback, type FeedbackBody } from '@/api/hooks';
import { useToast } from '@/components/Toast';
import { submitErrorMessage } from '@/lib/formErrors';

// Item 23: Help page, modelled on the CRM "Need Help?" page.
const SHAREPOINT_URL = 'https://brccpa.sharepoint.com/sites/PracticeManagement';

const FAQ: { q: string; a: string }[] = [
  { q: 'How do I set up a new client?', a: 'Go to New Client and choose New Master Client or New Sub of Existing Client. Fill in the required (*) fields and submit. Client Acceptance must be complete before you submit.' },
  { q: "What's the difference between Submit and Submit & Duplicate?", a: 'Submit sends the request and takes you back to the list. Submit & Duplicate sends it and keeps you on the form so you can enter the next one.' },
  { q: 'What happens after I submit a New Client or New Engagement?', a: "The Admin Team updates Maconomy, CCH Engagement and Axcess as needed. You'll get an email once your submission is completed." },
  { q: 'How do I find a client or engagement?', a: 'Use View / Change Clients or View / Change Engagements. Search by client name or ID; engagements can also be searched by engagement ID, template or service, and filtered by year.' },
  { q: "How do I change an existing client's details?", a: 'Open the client from View / Change Clients, make your changes and submit. The change goes to the Admin Team for review.' },
  { q: 'How do I change many clients or engagements at once?', a: "Use Mass Change Request under Forms. Pick the Clients or Engagements tab and choose up to 2,000: pick a master client and use Select All to add its sub-clients, and Add whole group adds every engagement of a client group. Download the template with their current details, change only what needs changing in Excel and upload the file. Any problems are listed by row and column, and nothing is sent until they're fixed. Then confirm the changes and submit. The file is sent as one Mass Change request, which is processed in Maconomy, and you get an email when it is completed." },
  { q: 'What can I change with Mass Change?', a: "Clients: the details in the template, such as name, contact, address, phone, emails, Client Manager and Bill to. Engagements: Practice Area Partner, Tax Signer, Engagement Manager, In-Charge, EL Signer, the invoice and statement emails, and Peer Review codes (1000 and 1010 engagements only). An engagement's Client Manager comes from the client, so change it on the client." },
  { q: 'How do I terminate a client that has sub-clients?', a: 'Use Client Termination. When you select a master client, its sub-clients are listed so you can choose which to terminate. If any stay active, pick one of them as the new master.' },
  { q: 'How do I stop one service without terminating the client?', a: 'Use Engagement Discontinuation and search for the engagement(s). The client stays active.' },
  { q: 'How do I bring back a terminated client?', a: 'Use Client Reactivation. Its search includes terminated clients.' },
  { q: 'Why is a screen greyed out with a "data load in progress" message?', a: 'The Maconomy data is being refreshed. Wait a few minutes for it to finish, then try again.' },
  { q: 'Should I update a client here or in the CRM?', a: 'Make client updates for Maconomy clients here. They flow to the CRM automatically when it refreshes.' },
];

const EMPTY: FeedbackBody = { type: 'Feedback', subject: '', details: '' };

export function Help() {
  const notify = useToast();
  const send = useSendFeedback();
  const [f, setF] = useState<FeedbackBody>(EMPTY);
  const invalid = !f.subject.trim() || !f.details.trim();

  async function onSubmit() {
    try {
      await send.mutateAsync({ ...f, subject: f.subject.trim(), details: f.details.trim() });
      notify('success', 'Thank you. Your feedback was sent to Amanda and Tyler, with a copy to you.');
      setF(EMPTY);
    } catch (e) {
      notify('error', submitErrorMessage(e));
    }
  }

  return (
    <div className="w-full max-w-3xl space-y-5 p-6">
      <div className="flex items-center gap-3">
        <HelpCircle className="h-5 w-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Not sure where to go? Start with the common questions below.</p>
      </div>

      {/* General CAD questions */}
      <div className="card">
        <div className="mb-3 flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">General CAD Questions</h2>
        </div>
        <div className="divide-y divide-border">
          {FAQ.map((x) => (
            <div key={x.q} className="py-3 first:pt-0">
              <p className="text-sm font-medium text-foreground">{x.q}</p>
              <p className="mt-1 text-sm text-muted-foreground">{x.a}</p>
            </div>
          ))}
        </div>
        <a href={SHAREPOINT_URL} target="_blank" rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1 text-sm text-accent hover:underline">
          For more help, see the Practice Management SharePoint <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Report an issue or bug (same wording as CRM, text only) */}
      <div className="card space-y-2">
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Report an issue or bug</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Create an IT Help Desk ticket and put "Dashboard" in the subject line so it reaches the right team.
        </p>
      </div>

      {/* Feedback or enhancement ideas: emailed to Amanda and Tyler */}
      <div className="card space-y-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Feedback or enhancement ideas</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Have an idea to make the dashboard better? Send it here. It goes to Amanda and Tyler, with a copy to you.
        </p>
        <div className="flex gap-6">
          {(['Feedback', 'Enhancement idea'] as const).map((t) => (
            <label key={t} className="flex items-center gap-2 text-sm">
              <input type="radio" name="fbType" checked={f.type === t} disabled={send.isPending}
                onChange={() => setF((s) => ({ ...s, type: t }))} />
              {t}
            </label>
          ))}
        </div>
        <div>
          <label className="label">Subject *</label>
          <input className="input" maxLength={200} value={f.subject} disabled={send.isPending}
            onChange={(e) => setF((s) => ({ ...s, subject: e.target.value }))} />
        </div>
        <div>
          <label className="label">Details *</label>
          <textarea className="input" rows={4} maxLength={4000} value={f.details} disabled={send.isPending}
            onChange={(e) => setF((s) => ({ ...s, details: e.target.value }))} />
        </div>
        <button type="button" className="btn-success" disabled={invalid || send.isPending} onClick={onSubmit}>
          {send.isPending ? 'Sending…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}
